/**
 * Application root component
 */

import { useState, useEffect, useRef } from "react";
import {
  AuthProvider,
  useAuth,
  NotificationProvider,
  ServiceFlowProvider,
} from "@/contexts";
import { Toaster } from "sileo";
import {
  LoginPage,
  DashboardPage,
  ClientsPage,
  CitiesPage,
  QuotesPage,
  TreatmentsPage,
  WorkOrdersPage,
  InventoryPage,
  InvoicingPage,
  ReportsPage,
  ServicesPage,
  ClientPortalPage,
  SecurityPage,
  ServiceRequestsPage,
} from "@/pages";
import { PublicQuote } from "@/pages/PublicQuote";
import { MainLayout } from "@/layouts";
import { Spinner } from "@/components/ui/Spinner";
import { serviceRequestsService } from "@/services/serviceRequestsService";
import "./App.css";

/**
 * Main content router based on authentication state
 */
function AppContent(): React.ReactElement {
  const { isAuthenticated, isLoading, user } = useAuth();
  const [currentPath, setCurrentPath] = useState("/dashboard");
  const [isUrgencyLoading, setIsUrgencyLoading] = useState(true);
  const [hasUrgentRequests, setHasUrgentRequests] = useState(false);
  const prevUserRef = useRef<{ id: string; role: string } | null>(null);

  // Check if we're on a public route
  useEffect(() => {
    const updatePath = () => {
      const path = window.location.pathname;
      if (path.startsWith("/public/")) {
        setCurrentPath(path);
      }
    };
    updatePath();
  }, []);

  // Set initial path based on user role
  useEffect(() => {
    const prevUser = prevUserRef.current;
    const currentUser = user ? { id: user.id, role: user.role } : null;

    // User changed (login, logout, or role change)
    const userChanged =
      (!prevUser && currentUser) || // Login
      (prevUser && !currentUser) || // Logout
      (prevUser &&
        currentUser &&
        (prevUser.id !== currentUser.id || prevUser.role !== currentUser.role)); // Different user or role

    if (userChanged) {
      if (!user) {
        // User logged out, reset to dashboard
        setCurrentPath("/dashboard");
      } else if (user.role === "client") {
        // Client users go to portal
        setCurrentPath("/client-portal");
      } else {
        // Admin/technician users go to dashboard
        setCurrentPath("/dashboard");
      }
    }

    // Update ref
    prevUserRef.current = currentUser;
  }, [user]);

  useEffect(() => {
    if (!isAuthenticated || user?.role === "client") {
      setHasUrgentRequests(false);
      setIsUrgencyLoading(false);
      return;
    }

    let isMounted = true;
    setIsUrgencyLoading(true);

    void serviceRequestsService
      .hasHighUrgency()
      .then((hasUrgent) => {
        if (!isMounted) return;
        setHasUrgentRequests(hasUrgent);
        setIsUrgencyLoading(false);
      })
      .catch(() => {
        if (!isMounted) return;
        setHasUrgentRequests(false);
        setIsUrgencyLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, user?.role]);

  // Public routes don't require authentication
  if (currentPath.startsWith("/public/")) {
    if (currentPath === "/public/quote") {
      return <PublicQuote />;
    }
  }

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="app-loading">
        <Spinner size="large" centered />
      </div>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // If user is a client, show only client portal (no admin layout)
  if (user?.role === "client") {
    return <ClientPortalPage />;
  }

  if (isUrgencyLoading) {
    return (
      <div className="app-loading">
        <Spinner size="large" centered />
      </div>
    );
  }

  // Page titles
  const pageTitles: Record<string, string> = {
    "/dashboard": "Resumen General",
    "/clients": "Clientes",
    "/cities": "Ciudades",
    "/quotes": "Presupuestos",
    "/services": "Catálogo de Servicios",
    "/treatments": "Plagas",
    "/workorders": "Órdenes de Trabajo",
    "/invoicing": "Facturación",
    "/inventory": "Inventario",
    "/reports": "Informes",
    "/requests": "Solicitudes de Servicio",
    "/client-portal": "Área Privada de Cliente",
    "/security": "Seguridad y Cumplimiento",
  };

  // Render current page
  const renderPage = () => {
    switch (currentPath) {
      case "/dashboard":
        return <DashboardPage onNavigate={setCurrentPath} />;
      case "/clients":
        return <ClientsPage />;
      case "/cities":
        return <CitiesPage />;
      case "/quotes":
        return <QuotesPage />;
      case "/services":
        return <ServicesPage />;
      case "/treatments":
        return <TreatmentsPage />;
      case "/workorders":
        return <WorkOrdersPage />;
      case "/inventory":
        return <InventoryPage />;
      case "/invoicing":
        return <InvoicingPage />;
      case "/reports":
        return <ReportsPage />;
      case "/requests":
        return <ServiceRequestsPage onNavigate={setCurrentPath} />;
      case "/client-portal":
        return <ClientPortalPage />;
      case "/security":
        return <SecurityPage />;
      default:
        return (
          <div style={{ padding: "40px", textAlign: "center" }}>
            <h2>Próximamente</h2>
            <p>Esta sección estará disponible pronto.</p>
          </div>
        );
    }
  };

  // Show main application content with layout
  return (
    <MainLayout
      title={pageTitles[currentPath] || "BioSAM"}
      currentPath={currentPath}
      onNavigate={setCurrentPath}
      initialHasUrgentRequests={hasUrgentRequests}
    >
      {renderPage()}
    </MainLayout>
  );
}

/**
 * Main application component with providers
 */
export function App(): React.ReactElement {
  return (
    <AuthProvider>
      <NotificationProvider>
        <ServiceFlowProvider>
          <AppContent />
        </ServiceFlowProvider>
        <Toaster position="top-right" />
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;
